import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Tippy from "@tippyjs/react";
import type { Instance as TippyInstance } from "tippy.js";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import "./account-popover-tippy.css";

import Avatar from "../avatar/Avatar";
import {
  GLOBAL_HEADER_TRANSITION,
  OVERLAY_BOTTOM_MARGIN,
  OVERLAY_HOVER_OPEN_DELAY_MS,
  useGlobalHeaderOffset,
} from "../global/headerOverlay";
import UserItem from "../user-item/UserItem";
// MileageBlock.jsx / ListItem.jsx は型宣言が無いため TS7016 になる（他ページと同事情）。
// @ts-expect-error -- 型宣言なしの JS モジュール
import MileageBlock from "../mileage-block/MileageBlock";
// @ts-expect-error -- 型宣言なしの JS モジュール
import ListItem from "../list-item/ListItem";
import Modal from "../modal/Modal";
import TransactionsModal from "../transactions-modal/TransactionsModal";
import {
  CARD_WIDTH_PX,
  CARD_HEIGHT_PX,
} from "../offer-wizard/OfferUserCard";
import WithdrawalRequestModal from "../withdrawal-request-modal/WithdrawalRequestModal";
import {
  useSignoutUserMutation,
  useGetCurrentUserQuery,
  // @ts-expect-error -- 型宣言なしの JS モジュール
} from "../../app/accounts/authuser";
import { useGetProfileSettingsQuery } from "../../app/services/creatorProfile";
import { useCreatePaymentRequestMutation } from "../../app/services/paymentRequests";
import {
  useGetTransactionsQuery,
  useGetWithdrawableTransactionsQuery,
} from "../../app/services/transactions";
import { computeDisplayBalances } from "../../utils/balance";
import { computeTransactionsNetExclusive } from "../../utils/transactions";
import { addConsumptionTax } from "../../utils/tax";
import { formatYenSigned } from "../../utils/money";

// 振込申請の最小額（BE の MIN_AMOUNT と一致、税抜の settled で判定）
const MIN_WITHDRAWAL_AMOUNT = 100;

// 振込に必要な銀行口座フィールド（GET /api/me のレスポンス名）。
const REQUIRED_BANK_FIELDS = [
  "bank",
  "bank_branch",
  "bank_branch_number",
  "account_type",
  "account_number",
  "account_holder",
];

// ── 型 ──

export interface AccountPopoverProps {
  /** GlobalHeader から渡される現在ユーザー（/api/me の data）。 */
  userData?: {
    role?: string;
    avatar_optimized?: string;
    avatar_original?: string;
    display_name_jp?: string;
    display_name_en?: string;
    stage_name?: string;
  };
}

// ── styled-components ──

/* Avatar は inline-block のため、block の中だとベースライン分の下余白ができて
 * ヘッダー内で上寄りになる。flex にして高さをアバターちょうどにする。 */
const AvatarTrigger = styled.div`
  display: flex;
  cursor: pointer;

  * {
    cursor: pointer !important;
  }
`;

const PopoverInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// ユーザーカード（UserItem variant="card"）をオファー作成時と同じ寸法
// （白銀比）で中央に置くためのスロット（オンボーディングの AccountCardSlot と同じ流儀）。
const AccountCardSlot = styled.div`
  margin: 16px auto 8px;
  width: ${CARD_WIDTH_PX}px;
  height: ${CARD_HEIGHT_PX}px;
  max-width: 100%;
`;

// config 画面の左スレッドと同じ ListItem メニューを縦に並べる領域。
const MenuList = styled.div`
  border-top: 1px solid var(--soremo-border);
`;

// 左端をアバターの左端、上端をヘッダーの下端にぴったり合わせる。Tippy 既定の
// preventOverflow は画面端から左右 5px 押し戻すため 0 にし、flip で上側へ反転させない。
const POPPER_OPTIONS = {
  strategy: "fixed" as const,
  modifiers: [
    { name: "preventOverflow", options: { padding: 0 } },
    { name: "flip", enabled: false },
  ],
};

// ── メインコンポーネント ──

const AccountPopover = ({ userData }: AccountPopoverProps) => {
  const navigate = useNavigate();
  const [signoutUser] = useSignoutUserMutation();
  const isAvatarEditingRef = useRef(false);
  const tippyRef = useRef<TippyInstance | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // ヘッダーの真下（プロジェクトバナーの上端と同じ高さ）から、ヘッダーが隠れて
  // いれば画面の一番上から出す。基準をアバター自体にすると、ヘッダーと一緒に
  // 画面外へ押し出されるため、横位置はアバター、縦位置はヘッダーの下端
  // （隠れていれば 0）の仮想的な基準矩形を使う。
  const headerOffset = useGlobalHeaderOffset();
  const headerOffsetRef = useRef(headerOffset);
  headerOffsetRef.current = headerOffset;

  const getReferenceClientRect = useCallback((): DOMRect => {
    const rect = triggerRef.current?.getBoundingClientRect();
    const top = headerOffsetRef.current;
    const left = rect?.left ?? 0;
    const width = rect?.width ?? 0;
    return new DOMRect(left, top, width, 0);
  }, []);

  // 画面に収まる限り中身をすべて表示する（上の位置と下端の余白を除いた高さ）。
  const applyMaxHeight = useCallback((instance: TippyInstance) => {
    instance.popper.style.setProperty(
      "--account-popover-max-height",
      `calc(100vh - ${headerOffsetRef.current + OVERLAY_BOTTOM_MARGIN}px)`,
    );
  }, []);

  useEffect(() => {
    const instance = tippyRef.current;
    if (!instance) return;
    applyMaxHeight(instance);
    instance.popperInstance?.update();
  }, [headerOffset, applyMaxHeight]);

  const isClient = userData?.role === "CLIENT";

  const { data: currentUser } = useGetCurrentUserQuery();
  const meData = currentUser?.data;
  const currentUserId = meData?.id ?? null;

  // マイサイト公開設定（vanity_slug）。ユーザーカードの QR に公開プロフィール URL を出す。
  // CLIENT はマイサイトを持たないため取得しない。
  const { data: settingsResp } = useGetProfileSettingsQuery(undefined, {
    skip: isClient,
  });
  const vanitySlug = settingsResp?.data?.vanity_slug || "";
  const profileQrUrl = vanitySlug ? `https://soremo.jp/${vanitySlug}` : "";

  // 新旧互換の税込合算残高（旧フィールドは税込のまま、新フィールド free/settled は×1.1して合算）
  const balances = useMemo(
    () => (meData ? computeDisplayBalances(meData) : null),
    [meData],
  );

  // 進行中取引（全プロジェクト横断）。一覧モーダル表示に使う。
  // CLIENT は表示しないため取得もスキップ。
  const { data: txData } = useGetTransactionsQuery(undefined, {
    skip: isClient,
  });
  const transactions = useMemo(() => txData?.data?.transactions ?? [], [txData]);

  // 振込申請対象（受託者として検収完了・未申請の新環境オファー）。
  // グローバルメニュー「振込申請」の確認リストに使う。CLIENT はスキップ。
  const { data: withdrawableData } = useGetWithdrawableTransactionsQuery(
    undefined,
    { skip: isClient },
  );
  const withdrawableTransactions =
    withdrawableData?.data?.transactions ?? [];

  // 振込申請額は、振込リストと同じレスポンスの settled_balance（税抜・申請原資）を
  // 税込換算して表示する。/api/me のキャッシュ残高だと、支出（オファー）控除後の
  // 最新 balance_settled と乖離してリスト合算と一致しないことがあるため、ここでは
  // 実際の申請原資（= リスト合算）に揃える。
  const withdrawSettledExclusive = Number(
    withdrawableData?.data?.settled_balance ?? 0,
  );
  const withdrawAmountLabel = `¥${addConsumptionTax(
    Number.isFinite(withdrawSettledExclusive) ? withdrawSettledExclusive : 0,
  ).toLocaleString()}`;

  // 進行中の対価合計（税込・差し引き）。BE が各取引に付与する display_amount
  // （受託=プラス / 委託=マイナス、税抜）を合算して税込換算する。進行中モーダル
  // 上部サマリと算出基盤（computeTransactionsNetExclusive）を共有するため、
  // ヘッダー表示額と一覧モーダルが必ず一致する。
  const inProgressAmount = useMemo(
    () => addConsumptionTax(computeTransactionsNetExclusive(transactions)),
    [transactions],
  );

  // 進行中行のクリック可否。一覧モーダルに表示できる取引があるときだけ
  // クリック可能にする（= 表示額が算出された取引があるとき）。
  const hasInProgress = transactions.length > 0;

  const [createPaymentRequest, { isLoading: isCreating }] =
    useCreatePaymentRequestMutation();

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferDone, setTransferDone] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [isBankRequiredModalOpen, setIsBankRequiredModalOpen] = useState(false);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);

  const hasBankInfo = meData
    ? REQUIRED_BANK_FIELDS.every((field) => !!meData[field])
    : false;
  // 振込申請の可否は新仕様 settled（税抜）で判定（API がそれを上限に処理する）
  const canWithdraw = balances
    ? balances.settledExclusive >= MIN_WITHDRAWAL_AMOUNT
    : false;

  // ホバーで開き、マウスが離れても閉じない。閉じるのは外側クリック・Esc・
  // メニュー操作（closePopover）のときだけ（soremon パネルと同じ開閉ルール）。
  const allowHideRef = useRef(false);
  const closePopover = useCallback(() => {
    allowHideRef.current = true;
    tippyRef.current?.hide();
  }, []);
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") closePopover();
    },
    [closePopover],
  );

  const handleOpenTransfer = useCallback(() => {
    closePopover();
    if (!canWithdraw) {
      toast.error("振込可能な残高がありません");
      return;
    }
    if (!hasBankInfo) {
      setIsBankRequiredModalOpen(true);
      return;
    }
    setTransferDone(false);
    setTransferError(null);
    setIsTransferModalOpen(true);
  }, [canWithdraw, hasBankInfo, closePopover]);

  const handleNavigateToBankAccount = useCallback(() => {
    setIsBankRequiredModalOpen(false);
    navigate("/my-page", { state: { selectedItem: "bankAccount" } });
  }, [navigate]);

  const handleConfirmTransfer = useCallback(async () => {
    setTransferError(null);
    try {
      // amount 省略 = settled 全額を申請
      await createPaymentRequest({}).unwrap();
      setTransferDone(true);
    } catch {
      setTransferError(
        "振込申請に失敗しました。時間をおいて再度お試しください。",
      );
    }
  }, [createPaymentRequest]);

  const handleOpenTransactions = useCallback(() => {
    closePopover();
    setIsTransactionsModalOpen(true);
  }, [closePopover]);

  const handleNavigateToMyPage = useCallback(() => {
    closePopover();
    navigate("/my-page");
  }, [navigate, closePopover]);

  // 共有ノート（参加権限のある全ノートを横断表示する /notes）へ
  const handleNavigateToNotes = useCallback(() => {
    closePopover();
    navigate("/notes");
  }, [navigate, closePopover]);

  // 共有ライブラリ（参加権限のある全ライブラリを横断表示する /library）へ
  const handleNavigateToLibrary = useCallback(() => {
    closePopover();
    navigate("/library");
  }, [navigate, closePopover]);

  // QR クリックでマイサイト編集ページへ。
  const handleNavigateToMySite = useCallback(() => {
    closePopover();
    navigate("/my-profile");
  }, [navigate, closePopover]);

  const handleSignout = useCallback(async () => {
    closePopover();
    try {
      await signoutUser().unwrap();
    } catch (error) {
      console.error("サインアウトエラー:", error);
    }
  }, [signoutUser, closePopover]);

  // ポップオーバーの中身
  const popoverContent = (
    <PopoverInner>
      {/* ユーザーカード（ユーザー登録オンボーディングと同じ variant="card"）。
          アバター / テキストで編集、QR クリックでマイサイト編集ページへ。 */}
      <AccountCardSlot>
        <UserItem
          size={96}
          editable={true}
          hideBadges={true}
          variant="card"
          qrUrl={profileQrUrl || undefined}
          onQrClick={handleNavigateToMySite}
          onAvatarEditingChange={(editing) => {
            isAvatarEditingRef.current = editing;
          }}
        />
      </AccountCardSlot>

      {/* マイレージランク */}
      <MileageBlock />

      {/* config 画面の左スレッドと同じメニューアイテム UI */}
      <MenuList>
        {!isClient && (
          <>
            <ListItem
              name="振込申請"
              status={
                balances
                  ? `¥${balances.withdrawable.toLocaleString()}`
                  : undefined
              }
              onClick={canWithdraw ? handleOpenTransfer : undefined}
              interactive={canWithdraw}
            />
            <ListItem
              name="進行中"
              status={formatYenSigned(inProgressAmount)}
              onClick={hasInProgress ? handleOpenTransactions : undefined}
              interactive={hasInProgress}
            />
          </>
        )}
        <ListItem name="マイページ" onClick={handleNavigateToMyPage} />
        <ListItem name="共有ノート" onClick={handleNavigateToNotes} />
        <ListItem name="共有ライブラリ" onClick={handleNavigateToLibrary} />
        <ListItem
          name="サインアウト"
          onClick={handleSignout}
          hasBorderBottom={false}
        />
      </MenuList>
    </PopoverInner>
  );

  return (
    <>
      <Tippy
        theme="account-popover"
        animation="scale"
        duration={[150, 100]}
        content={popoverContent}
        trigger="mouseenter click"
        interactive={true}
        interactiveBorder={16}
        delay={[OVERLAY_HOVER_OPEN_DELAY_MS, 0]}
        placement="bottom-start"
        arrow={false}
        offset={[0, 0]}
        zIndex={100}
        appendTo={() => document.body}
        getReferenceClientRect={getReferenceClientRect}
        popperOptions={POPPER_OPTIONS}
        onCreate={(instance) => {
          tippyRef.current = instance;
        }}
        onShow={(instance) => {
          applyMaxHeight(instance);
          document.addEventListener("keydown", handleEscape);
        }}
        onShown={(instance) => {
          // 開いている間だけ、ヘッダーの出入りに合わせて位置をなめらかに追従させる
          // （開く瞬間の初期配置はアニメーションさせない）。
          instance.popper.style.transition = `transform ${GLOBAL_HEADER_TRANSITION}`;
        }}
        onClickOutside={() => {
          allowHideRef.current = true;
        }}
        onHide={() => {
          if (isAvatarEditingRef.current || !allowHideRef.current) return false;
          allowHideRef.current = false;
          document.removeEventListener("keydown", handleEscape);
        }}
        onHidden={(instance) => {
          instance.popper.style.transition = "";
        }}
      >
        <AvatarTrigger ref={triggerRef}>
          <Avatar
            size={40}
            src={userData?.avatar_optimized || userData?.avatar_original || ""}
            name={
              userData?.display_name_jp ||
              userData?.display_name_en ||
              userData?.stage_name
            }
          />
        </AvatarTrigger>
      </Tippy>

      {isBankRequiredModalOpen && (
        <Modal
          onClose={() => setIsBankRequiredModalOpen(false)}
          title="振込先銀行口座の登録が必要です"
          description="振込申請を行うには、振込先銀行口座の登録が必要です。マイページで口座情報を登録してください。"
          primaryAction={{
            label: "口座を登録する",
            onClick: handleNavigateToBankAccount,
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setIsBankRequiredModalOpen(false),
            theme: "secondary",
            autoFocus: true,
          }}
        />
      )}

      {/* 振込申請モーダル（Tippy 外。メニューを閉じても表示を維持する）。
          対象オファーをリスト表示し、内容を確認した上で「振込申請 >」する。
          申請完了後は done=true で完了表示に切り替わる。 */}
      {isTransferModalOpen && (
        <WithdrawalRequestModal
          transactions={withdrawableTransactions}
          currentUserId={currentUserId}
          amountLabel={withdrawAmountLabel}
          isSubmitting={isCreating}
          done={transferDone}
          error={transferError}
          bankName={meData?.bank}
          bankBranch={meData?.bank_branch}
          onSubmit={handleConfirmTransfer}
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}

      {/* 進行中取引の一覧モーダル（自分の残高なのでチャージ可） */}
      {isTransactionsModalOpen && (
        <TransactionsModal
          transactions={transactions}
          currentUserId={currentUserId}
          balanceSource={meData}
          allowCharge
          onClose={() => setIsTransactionsModalOpen(false)}
        />
      )}
    </>
  );
};

export default AccountPopover;
